import { Box, SxProps, useMediaQuery } from "@mui/material";

import React, { FC, useMemo } from "react";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors-twentyfive";
import useHash from "@src/hooks/hash";
import { ArcherElement } from "react-archer";
import { motion } from "motion/react";
import { useTheme } from "@mui/material/styles";
import NavItemRegular from "./nav-item-regular";
import NavItemDimension from "./nav-item-dimension";
import NavItemRoot from "./nav-item-root";

interface Props {
  navItem: any;
  currentDepth?: number;
  fullscreen?: boolean;
}

const NavItem: FC<Props> = ({
  navItem,
  currentDepth = 0,
  fullscreen = false,
}) => {
  const hash = useHash();
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));
  const isMedium = useMediaQuery(theme.breakpoints.up("md"));

  const hashDebounce = useMemo(() => {
    if (navItem?.name === "root") {
      return [];
    }

    return (
      [
        // Children to selected node
        isMedium &&
          navItem?.parent?.data?.categoryId === (hash ? hash : "root") && {
            targetId: navItem?.parent?.data?.categoryId,
            targetAnchor: "right",
            sourceAnchor: "left",
            className: "taxonomy-arrow",
            style: {
              strokeColor: Colors.AiOutline,
              strokeWidth: 1,
              endMarker: false,
            },
          },
        // Selected node to parent
        ((isMedium && navItem?.data?.categoryId === (hash ? hash : "root")) ||
          fullscreen) && {
          targetId: navItem?.parent?.data?.categoryId,
          targetAnchor: "right",
          sourceAnchor: "left",
          className: "taxonomy-arrow",
          style: {
            strokeColor: Colors.AiOutline,
            strokeWidth: 1,
            endMarker: false,
          },
        },
        ((isMedium &&
          navItem?.parent?.data?.selectedParent === true &&
          navItem?.data?.selectedParent === true) ||
          fullscreen) && {
          targetId: navItem?.parent?.data?.categoryId,
          targetAnchor: "right",
          sourceAnchor: "left",
          className: "taxonomy-arrow",
          style: {
            strokeColor: Colors.AiOutline,
            strokeWidth: 1,
            endMarker: false,
          },
        },
      ].filter(Boolean) ?? []
    );
  }, [navItem, isMedium]);

  const baseStyles: SxProps = {
    display: "flex",
    position: "relative",
    borderRadius: "100px",
    border: `solid 1px ${Colors.AiOutline}`,
    fontSize: {
      xs: "12px",
      md: "14px",
      lg: "15px",
    },
    fontWeight: 400,
    paddingX: "20px",
    minHeight: {
      xs: "30px",
      md: "40px",
    },
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    textDecoration: "none",
    width: {
      xs: "300px",
      md: "370px",
      lg: "auto",
    },
  };

  const defaultStyles: SxProps = {
    ...baseStyles,
    color: Colors.PrimaryContrast,
    "&:hover": {
      cursor: "pointer",
      color: Colors.PrimaryContrast,
    },
    "&:visited": {
      color: Colors.PrimaryContrast,
    },
  };

  const previewStyles: SxProps = {
    ...defaultStyles,
    opacity: 0.5,
  };

  const selectedStyles: SxProps = {
    ...baseStyles,
    color: "#0F0D13",
    fontWeight: 600,
    backgroundImage: "linear-gradient(90deg, #FFCDAF 12.35%, #8C48EB 88.07%)",
    "&:hover": {
      cursor: "pointer",
      color: "#0F0D13",
    },
    "&:visited": {
      color: "#0F0D13",
    },
  };

  function calculateOpacity() {
    if (fullscreen) {
      return 1;
    }

    if (navItem.data.hidden) {
      return 0;
    }

    if (
      navItem.data.depth <= currentDepth &&
      (hash ? hash : "root") !== navItem.data.categoryId
    ) {
      return 0.5;
    }

    return 1;
  }

  const opacity = calculateOpacity();

  return (
    <Box
      key={navItem.data.categoryId}
      component={motion.div}
      initial={{
        opacity: 0,
      }}
      animate={{
        display: navItem.data.hidden && !fullscreen ? "none" : "flex",
        opacity: navItem.data.categoryId === "root" ? 1 : opacity,
      }}
      transition={{
        duration: 0.3,
      }}
      style={{
        justifyContent: "center",
      }}
      sx={{
        marginBottom: {
          xs: "8px",
          md: "16px",
        },
        ":last-child": {
          marginBottom: "0px",
        },
        width: {
          xs: "300px",
          md: "370px",
          lg: "500px",
        },
      }}
    >
      <ArcherElement
        id={navItem.data.categoryId}
        relations={(hashDebounce ?? []) as any[]}
      >
        <div>
          {navItem?.data.categoryId === "root" ? (
            <NavItemRoot navItem={navItem} />
          ) : navItem?.data.categoryId === "IM" ||
            navItem?.data.categoryId === "PT" ? (
            <NavItemDimension navItem={navItem} currentDepth={currentDepth} />
          ) : (
            <NavItemRegular navItem={navItem} />
          )}
        </div>
      </ArcherElement>
    </Box>
  );
};

export default NavItem;
